import { z } from 'zod';
import { MOBILE_PATTERN } from '@/lib/forms';

/**
 * Mirrors `loginSchema` in the backend's auth.validation.ts.
 *
 * Validating here too means the user sees the mistake instantly instead of
 * after a round trip. The server still validates — client rules are for
 * convenience, never for security.
 */
export const loginSchema = z.object({
  identifier: z
    .string()
    .trim()
    .min(1, 'Please enter your email or mobile number')
    .max(150, 'That is too long'),
  password: z.string().min(1, 'Please enter your password').max(128, 'That is too long'),
  rememberMe: z.boolean().default(false),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

/** How the person chose to identify themselves on the sign-in form. */
export const SIGN_IN_METHODS = ['EMAIL', 'MOBILE'] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];

/**
 * The sign-in form's own shape: the request plus the Email / Mobile switch.
 *
 * The switch only decides which check the box gets — the server still takes one
 * `identifier` and tells the two apart itself, so `method` is never sent.
 */
export const signInFormSchema = loginSchema
  .extend({ method: z.enum(SIGN_IN_METHODS) })
  .superRefine((values, ctx) => {
    if (values.identifier === '') return;
    if (values.method === 'EMAIL' && !z.string().email().safeParse(values.identifier).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['identifier'],
        message: 'Enter a valid email address',
      });
    }
    if (values.method === 'MOBILE' && !MOBILE_PATTERN.test(values.identifier)) {
      ctx.addIssue({
        code: 'custom',
        path: ['identifier'],
        message: 'Enter a valid 10-digit mobile number',
      });
    }
  });

export type SignInFormValues = z.infer<typeof signInFormSchema>;
